import * as React from "react"
import { Input } from "@/components/ui/input"

/**
 * DecimalInput - A reusable input component for decimal numbers (currency, quantities, etc.)
 *
 * Behavior:
 * - Type "10" → displays "10" while typing, "10.00" on blur
 * - Type "10.5" → displays "10.5" while typing, "10.50" on blur
 * - Type ".50" → displays ".50" while typing, "0.50" on blur
 * - Allows natural decimal number entry without automatic formatting during typing
 * - With autoClearValue: clears field on focus if it matches the default value
 *
 * @param {Object} props
 * @param {number|string} props.value - The current value
 * @param {Function} props.onChange - Callback when value changes (receives number)
 * @param {number} props.decimalPlaces - Number of decimal places to show (default: 2)
 * @param {number} props.min - Minimum value (default: 0)
 * @param {number} props.max - Maximum value (optional)
 * @param {number} props.step - Step increment (default: calculated from decimalPlaces)
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.required - Whether field is required
 * @param {string|number} props.autoClearValue - Value to clear on focus (e.g., "0.00", "1.00")
 * @param {string} props.className - Additional CSS classes
 */
const DecimalInput = React.forwardRef(({
  value,
  onChange,
  decimalPlaces = 2,
  min = 0,
  max,
  step,
  placeholder,
  required = false,
  autoClearValue,
  className,
  ...props
}, ref) => {
  const [displayValue, setDisplayValue] = React.useState('')
  const [isFocused, setIsFocused] = React.useState(false)

  // Calculate step if not provided
  const calculatedStep = step || (1 / Math.pow(10, decimalPlaces))

  // Update display value when external value changes
  React.useEffect(() => {
    if (!isFocused) {
      // When not focused, format with decimal places
      if (value === '' || value === null || value === undefined) {
        setDisplayValue('')
      } else {
        const numValue = typeof value === 'string' ? parseFloat(value) : value
        if (!isNaN(numValue)) {
          setDisplayValue(numValue.toFixed(decimalPlaces))
        } else {
          setDisplayValue('')
        }
      }
    }
  }, [value, isFocused, decimalPlaces])

  const handleFocus = (e) => {
    setIsFocused(true)

    // Auto-clear pattern: if autoClearValue is set and value matches it, clear the field
    if (autoClearValue !== undefined) {
      const autoClearString = String(autoClearValue)
      const currentValueString = value !== null && value !== undefined ? String(value) : ''

      if (currentValueString === autoClearString || currentValueString === '') {
        setDisplayValue('')
        return
      }
    }

    // Show raw value when focused for easier editing
    if (value !== '' && value !== null && value !== undefined) {
      const numValue = typeof value === 'string' ? parseFloat(value) : value
      if (!isNaN(numValue)) {
        // Remove trailing zeros for easier editing
        setDisplayValue(numValue.toString())
      }
    }
  }

  const handleBlur = (e) => {
    setIsFocused(false)
    const inputValue = e.target.value.trim()

    if (inputValue === '' || inputValue === '.') {
      // Auto-clear pattern: restore autoClearValue if field is empty
      if (autoClearValue !== undefined) {
        const autoClearString = String(autoClearValue)
        const autoClearNum = parseFloat(autoClearString)
        if (!isNaN(autoClearNum)) {
          setDisplayValue(autoClearString)
          onChange?.(autoClearNum)
          return
        }
      }

      setDisplayValue('')
      onChange?.(null)
      return
    }

    const numValue = parseFloat(inputValue)

    if (!isNaN(numValue)) {
      // Clamp to min/max if provided
      let clampedValue = numValue
      if (min !== undefined && clampedValue < min) clampedValue = min
      if (max !== undefined && clampedValue > max) clampedValue = max

      // Format and update
      setDisplayValue(clampedValue.toFixed(decimalPlaces))
      onChange?.(clampedValue)
    } else {
      // Invalid input, revert to previous value
      if (value !== '' && value !== null && value !== undefined) {
        const numValue = typeof value === 'string' ? parseFloat(value) : value
        setDisplayValue(numValue.toFixed(decimalPlaces))
      } else {
        setDisplayValue('')
      }
    }
  }

  const handleChange = (e) => {
    const inputValue = e.target.value
    setDisplayValue(inputValue)

    // Parse and send numeric value to parent
    if (inputValue === '' || inputValue === '.') {
      onChange?.(null)
      return
    }

    const numValue = parseFloat(inputValue)
    if (!isNaN(numValue)) {
      onChange?.(numValue)
    }
  }

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      required={required}
      className={className}
      {...props}
    />
  )
})

DecimalInput.displayName = "DecimalInput"

export { DecimalInput }
